import React, { MutableRefObject, Ref, ReactNode } from 'react';

/**
 * Clones valid React elements and applies additional props to them.
 *
 * @param children - The child nodes to process.
 * @param extraProps - Additional props to be passed to valid React elements.
 * @returns A ReactNode with extra props applied to valid elements, leaving others unchanged.
 */
export const applyPropsToChildren = (children: ReactNode, extraProps?: Record<string, any>): ReactNode => {
  if (!extraProps) return children;

  return React.Children.map(children, (child) =>
    React.isValidElement(child) ? React.cloneElement(child, extraProps) : child
  );
};

/**
 * Utility function to merge multiple refs into a single ref callback function.
 *
 * @param refs - Refs to be merged.
 * @returns A callback function that sets the value on all merged refs.
 */
export const mergeRefs = <T,>(...refs: (Ref<T> | undefined)[]): Ref<T> => {
  return (value: T) => {
    refs.forEach(ref => {
      if (!ref) return; // Skip null/undefined refs
      
      if (typeof ref === 'function') {
        // If the ref is a function, call it with the value
        // Special handling for flatpickr which doesn't handle null values properly
        if (value === null || value === undefined) {
          // Check if this might be a flatpickr ref by examining the function
          const refString = ref.toString();
          if (refString.includes('flatpickr') || refString.includes('createFlatpickrInstance')) {
            // Skip calling flatpickr with null - it will crash
            // Flatpickr cleanup should be handled by the component itself
            return;
          }
        }
        
        // For all other refs, call normally
        ref(value);
      } else if (typeof ref === 'object' && 'current' in ref) {
        // If the ref is an object (MutableRefObject), assign the value to its current property
        (ref as MutableRefObject<T>).current = value;
      }
    });
  };
};
